import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NetworkInsights = () => {
  // Mock data - in a real app, this would come from analytics
  const insights = {
    connectionGrowth: '+12%',
    topGenres: ['Électronique', 'Jazz', 'Pop'],
    networkReach: '2,543',
    pendingRequests: 3,
    mutualConnections: 47,
    profileViews: '+23%'
  };

  const networkStats = [
    {
      label: 'Connexions totales',
      value: '127',
      change: '+5 cette semaine',
      icon: 'Users',
      color: 'text-blue-600'
    },
    {
      label: 'Portée du réseau',
      value: insights?.networkReach,
      change: 'Connexions de 2e niveau',
      icon: 'Network',
      color: 'text-green-600'
    },
    {
      label: 'Vues de profil',
      value: '89',
      change: insights?.profileViews + ' ce mois',
      icon: 'Eye',
      color: 'text-purple-600'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Network Growth */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Votre Réseau</h3>
          <Icon name="TrendingUp" size={20} className="text-green-500" />
        </div>

        <div className="space-y-4">
          {networkStats?.map((stat, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-muted ${stat?.color}`}>
                  <Icon name={stat?.icon} size={16} />
                </div>
                <div>
                  <div className="font-semibold text-foreground">{stat?.value}</div>
                  <div className="text-xs text-muted-foreground">{stat?.label}</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground text-right">
                {stat?.change}
              </div>
            </div>
          ))}
        </div>

        {insights?.pendingRequests > 0 && (
          <div className="mt-4 p-3 bg-accent/10 border border-accent/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-accent" />
                <span className="text-sm text-accent-foreground">
                  {insights?.pendingRequests} demandes en attente
                </span>
              </div>
              <Button variant="ghost" size="xs" className="text-accent hover:text-accent/80">
                Voir
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Top Genres in Network */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Genres populaires dans votre réseau
        </h3>
        
        <div className="space-y-3">
          {insights?.topGenres?.map((genre, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm text-foreground">{genre}</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-accent rounded-full transition-all duration-500"
                    style={{ width: `${85 - (index * 20)}%` }}
                  ></div>
                </div>
                <span className="text-xs text-muted-foreground w-8">
                  {85 - (index * 20)}%
                </span>
              </div>
            </div>
          ))}
        </div>

        <Button variant="ghost" size="sm" className="w-full mt-4 text-muted-foreground hover:text-foreground">
          Voir plus de statistiques
        </Button>
      </div>
      {/* Networking Tips */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Lightbulb" size={18} className="text-yellow-500" />
          <h3 className="text-lg font-semibold text-foreground">Conseil du jour</h3>
        </div>
        
        <div className="space-y-4">
          <div className="p-4 bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-lg">
            <h4 className="font-medium text-foreground mb-2">
              💡 Optimisez vos connexions
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Les profils avec des portfolios complets reçoivent 3x plus de demandes de connexion.
            </p>
            <Button variant="outline" size="xs">
              Compléter mon profil
            </Button>
          </div>

          <div className="text-xs text-muted-foreground space-y-2">
            <div className="flex items-start space-x-2">
              <div className="w-1 h-1 bg-accent rounded-full mt-2 flex-shrink-0"></div>
              <span>Personnalisez vos messages de connexion pour un taux de réponse 40% plus élevé</span>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1 h-1 bg-accent rounded-full mt-2 flex-shrink-0"></div>
              <span>Interagissez avec les posts de vos connexions pour rester visible</span>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1 h-1 bg-accent rounded-full mt-2 flex-shrink-0"></div>
              <span>Partagez votre expertise pour attirer de nouvelles connexions</span>
            </div>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Actions rapides</h3>
        
        <div className="space-y-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            iconName="UserPlus"
          >
            Inviter par email
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            iconName="Share"
          >
            Partager mon profil
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            iconName="MessageCircle"
          >
            Messages en attente ({insights?.pendingRequests})
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            iconName="Calendar"
          >
            Programmer un networking
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NetworkInsights;